// InvoiceDetails.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import TaleButton from "@components/TaleButton";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { InternalServerError } from "@components/Errors/InternalServerError";
import { invoicesService } from "@/api/invoices";
import { downloadInvoicePDF, openInvoicePDF } from "@/utils/InvoicePDFGenerator";
import { useCredentials } from "@/hooks/useCredentials";
import "./invoices.css";

const InvoiceDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const companyId = user?.id;

    const [isPrinting, setIsPrinting] = useState(false);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    // Fetch invoice data
    const {
        data: invoice,
        isLoading: invoiceLoading,
        error,
    } = useQuery({
        queryKey: ["invoice", id],
        queryFn: () => invoicesService.getInvoice(id),
        retry: false,
    });

    // Fetch company credentials (Émetteur/Issuer info)
    const {
        data: credentials,
        isLoading: credentialsLoading
    } = useCredentials();

    const isLoading = invoiceLoading || credentialsLoading;

    // Check if invoice belongs to current company
    useEffect(() => {
        if (invoice && companyId) {
            if ((invoice.company?.id || invoice.user?.id) !== companyId) {
                alert("Vous n'avez pas accès à cette facture");
                navigate("/factures");
            }
        }
    }, [invoice, companyId, navigate]);

    const getStatusColor = (status) => {
        switch (status) {
            case "PAID": return "bg-green-100 text-green-800";
            case "SUBMITTED": return "bg-yellow-100 text-yellow-800";
            case "REJECTED": return "bg-red-100 text-red-800";
            case "REFUNDED": return "bg-blue-100 text-blue-800";
            case "OVERDUE": return "bg-red-100 text-red-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case "PAID": return "Payée";
            case "SUBMITTED": return "En attente de paiement";
            case "REJECTED": return "Rejetée";
            case "REFUNDED": return "Remboursée";
            case "OVERDUE": return "En retard";
            default: return status || "En attente";
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-TN', {
            style: 'currency',
            currency: 'TND'
        }).format(amount || 0);
    };

    const handleDownloadPDF = async () => {
        if (!invoice) return;

        try {
            setIsGeneratingPDF(true);
            await downloadInvoicePDF(invoice, `Facture-${invoice.id}.pdf`);
        } catch (error) {
            console.error("Failed to generate PDF:", error);
            alert("Erreur lors de la génération du PDF");
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const handlePreviewPDF = async () => {
        if (!invoice) return;

        try {
            setIsGeneratingPDF(true);
            await openInvoicePDF(invoice);
        } catch (error) {
            console.error("Failed to preview PDF:", error);
            alert("Erreur lors de l'ouverture du PDF");
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const handlePayInvoice = () => {
        navigate(`/factures/${id}/paiement`);
    };

    const isPayable = invoice?.status === 'SUBMITTED' || invoice?.status === 'OVERDUE';

    // Calculate totals for display
    const calculateTotals = () => {
        if (!invoice) return { subtotal: 0, totalDiscount: 0, afterDiscount: 0, total: 0 };

        const items = invoice.items || [];
        const subtotal = items.reduce((sum, item) => {
            const unitPrice = parseFloat(item.unitPrice) || 0;
            const quantity = parseInt(item.quantity) || 0;
            return sum + (unitPrice * quantity);
        }, 0);

        const totalDiscount = items.reduce((sum, item) => {
            return sum + (parseFloat(item.discountValue) || 0);
        }, 0);

        const afterDiscount = subtotal - totalDiscount;
        const taxAmount = parseFloat(invoice.tax) || 0;
        const total = afterDiscount + taxAmount;

        return { subtotal, totalDiscount, afterDiscount, taxAmount, total };
    };

    useEffect(() => {
        const handleAfterPrint = () => {
            setIsPrinting(false);
        };

        window.addEventListener('afterprint', handleAfterPrint);
        return () => {
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, []);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <TaleLoadingFallback />
            </div>
        );
    }

    if (error) {
        return <InternalServerError />;
    }

    if (!invoice) {
        return (
            <div className="p-6">
                <TaleDashbaordMainCard className="p-6">
                    <div className="text-center py-8">
                        <i className="fas fa-file-invoice text-4xl text-gray-300 mb-4"></i>
                        <h2 className="text-xl font-semibold text-gray-700 mb-2">
                            Facture non trouvée
                        </h2>
                        <p className="text-gray-600 mb-6">
                            La facture demandée n'existe pas ou vous n'y avez pas accès.
                        </p>
                        <TaleButton onClick={() => navigate("/factures")}>
                            Retour aux factures
                        </TaleButton>
                    </div>
                </TaleDashbaordMainCard>
            </div>
        );
    }

    const { subtotal, totalDiscount, afterDiscount, taxAmount, total } = calculateTotals();

    // Helper function to split address lines for display
    const getAddressLines = (address) => {
        if (!address) return [];
        return address.split(',').map(line => line.trim());
    };

    return (
        <div className={`p-6 ${isPrinting ? "print-mode" : ""}`}>
            {/* Print styles */}
            <style>
                {`
          @media print {
            .no-print { display: none !important; }
            .print-mode * { color: black !important; }
            .print-container { 
              box-shadow: none !important; 
              border: 1px solid #ccc !important;
            }
            .print-table {
              border-collapse: collapse;
              width: 100%;
            }
            .print-table th, .print-table td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            .print-table th {
              background-color: #f2f2f2;
            }
          }
        `}
            </style>

            {/* Invoice Content */}
            <TaleDashbaordMainCard className="print-container">
                <div className="no-print mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h1 className="text-3xl font-bold">Facture #{invoice.id}</h1>
                            <div className="flex items-center gap-4 mt-2">
                                <span className={`px-3 py-1 rounded-full ${getStatusColor(invoice.status)}`}>
                                    {getStatusText(invoice.status)}
                                </span>
                                <span className="text-gray-600">
                                    Créée le {formatDate(invoice.creationDate)}
                                </span>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <TaleButton onClick={() => navigate("/factures")}>
                                <i className="fas fa-arrow-left mr-2"></i>
                                Retour
                            </TaleButton>
                            {isPayable && (
                                <TaleButton
                                    onClick={handlePayInvoice}
                                    className="bg-green-600 hover:bg-green-700 border-green-600"
                                >
                                    <i className="fas fa-credit-card mr-2"></i>
                                    Payer maintenant
                                </TaleButton>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-6">
                        <TaleButton
                            onClick={handleDownloadPDF}
                            loading={isGeneratingPDF}
                            disabled={isGeneratingPDF}
                        >
                            <i className="fas fa-download mr-2"></i>
                            Télécharger PDF
                        </TaleButton>

                        <TaleButton
                            variant="outlined"
                            onClick={handlePreviewPDF}
                            loading={isGeneratingPDF}
                            disabled={isGeneratingPDF}
                        >
                            <i className="fas fa-eye mr-2"></i>
                            Prévisualiser PDF
                        </TaleButton>

                        {/* Overdue reminder */}
                        {invoice.status === "OVERDUE" && (
                            <TaleButton
                                variant="outlined"
                                className="!bg-red-50 !text-red-700"
                                onClick={() => alert("Contactez l'administration pour plus d'informations")}
                            >
                                <i className="fas fa-exclamation-triangle mr-2"></i>
                                Facture échue
                            </TaleButton>
                        )}

                        {/* Payment method info for paid invoices */}
                        {invoice.status === "PAID" && invoice.paymentMethod && (
                            <div className="ml-auto flex items-center bg-green-50 px-4 py-2 rounded-lg">
                                <i className="fas fa-check-circle text-green-600 mr-2"></i>
                                <span className="text-green-700">Payée via {invoice.paymentMethod}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Header - Company credentials (Admin company) */}
                <div className="flex justify-between mb-8 pb-6 border-b">
                    <div>
                        <h2 className="text-2xl font-bold">FACTURE</h2>
                        <p className="text-gray-600 font-medium">{credentials?.companyName || "Administration"}</p>

                        {credentials?.address && getAddressLines(credentials.address).map((line, index) => (
                            <p key={index} className="text-gray-600">{line}</p>
                        ))}

                        {credentials?.phoneNumber && (
                            <p className="text-gray-600">Tél: {credentials.phoneNumber}</p>
                        )}

                        {credentials?.taxId && (
                            <p className="text-gray-600">TVA: {credentials.taxId}</p>
                        )}
                    </div>

                    <div className="text-right">
                        <div className="text-3xl font-bold mb-2">Facture #{invoice.id}</div>
                        <div className="text-gray-600">
                            Date d'émission: {formatDate(invoice.creationDate)}
                        </div>
                        {invoice.dueDate && (
                            <div className={`text-gray-600 ${invoice.status === 'OVERDUE' ? 'text-red-600 font-medium' : ''}`}>
                                Date d'échéance: {formatDate(invoice.dueDate)}
                                {invoice.status === 'OVERDUE' && (
                                    <span className="ml-2 text-sm bg-red-100 text-red-800 px-2 py-1 rounded">
                                        Échue
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* From/To */}
                <div className="grid grid-cols-2 gap-8 mb-8">
                    {/* Émetteur - Admin company from credentials */}
                    <div>
                        <h3 className="font-bold text-lg mb-2">Émetteur</h3>
                        <p className="font-bold">{credentials?.companyName || "Administration"}</p>

                        {credentials?.address && getAddressLines(credentials.address).map((line, index) => (
                            <p key={index}>{line}</p>
                        ))}

                        {credentials?.phoneNumber && (
                            <p>Tél: {credentials.phoneNumber}</p>
                        )}

                        {credentials?.taxId && (
                            <p>TVA: {credentials.taxId}</p>
                        )}
                    </div>

                    {/* Client - Your company (current user) */}
                    <div>
                        <h3 className="font-bold text-lg mb-2">Client</h3>
                        <p className="font-bold">{user?.companyName || "Votre entreprise"}</p>
                        {user?.email && (
                            <p>{user.email}</p>
                        )}
                        {user?.address && (
                            <p>{user.address}</p>
                        )}
                        {user?.phoneNumber && (
                            <p>Tél: {user.phoneNumber}</p>
                        )}
                        {user?.taxId && (
                            <p>TVA: {user.taxId}</p>
                        )}
                    </div>
                </div>

                {/* Items Table */}
                <div className="mb-8">
                    <table className="w-full border-collapse print-table">
                        <thead>
                        <tr className="bg-gray-50">
                            <th className="border p-3 text-left">Article</th>
                            <th className="border p-3 text-left">Quantité</th>
                            <th className="border p-3 text-left">Prix unitaire</th>
                            <th className="border p-3 text-left">Remise %</th>
                            <th className="border p-3 text-left">Montant remise</th>
                            <th className="border p-3 text-left">Total</th>
                        </tr>
                        </thead>
                        <tbody>
                        {(invoice.items || []).map((item, index) => {
                            const unitPrice = parseFloat(item.unitPrice) || 0;
                            const quantity = parseInt(item.quantity) || 0;
                            const discountPercentage = parseFloat(item.discountPercentage) || 0;
                            const discountValue = parseFloat(item.discountValue) || 0;
                            const itemTotal = (unitPrice * quantity) - discountValue;

                            return (
                                <tr key={index} className="hover:bg-gray-50">
                                    <td className="border p-3">{item.name}</td>
                                    <td className="border p-3 text-center">{quantity}</td>
                                    <td className="border p-3">{formatCurrency(unitPrice)}</td>
                                    <td className="border p-3 text-center">{discountPercentage}%</td>
                                    <td className="border p-3">{formatCurrency(discountValue)}</td>
                                    <td className="border p-3 font-semibold">
                                        {formatCurrency(itemTotal)}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>

                {/* Summary */}
                <div className="flex justify-end">
                    <div className="w-64">
                        <div className="flex justify-between mb-2">
                            <span>Sous-total:</span>
                            <span>{formatCurrency(subtotal)}</span>
                        </div>

                        {totalDiscount > 0 && (
                            <>
                                <div className="flex justify-between mb-2 text-green-600">
                                    <span>Remise totale:</span>
                                    <span>-{formatCurrency(totalDiscount)}</span>
                                </div>
                                <div className="flex justify-between mb-2 pt-2 border-t">
                                    <span>Montant après remise:</span>
                                    <span className="font-semibold">{formatCurrency(afterDiscount)}</span>
                                </div>
                            </>
                        )}

                        {taxAmount > 0 && (
                            <div className="flex justify-between mb-2">
                                <span>TVA:</span>
                                <span>{formatCurrency(taxAmount)}</span>
                            </div>
                        )}

                        {invoice.discountAmount > 0 && (
                            <div className="flex justify-between mb-2 text-green-600">
                                <span>Remise globale:</span>
                                <span>-{formatCurrency(invoice.discountAmount)}</span>
                            </div>
                        )}

                        <div className="flex justify-between text-lg font-bold pt-2 border-t">
                            <span>Total TTC:</span>
                            <span className="text-blue-600">{formatCurrency(total)}</span>
                        </div>
                    </div>
                </div>

                {/* Status and Payment Details */}
                <div className="mt-8 pt-6 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="font-bold text-lg mb-3">Statut</h3>
                            <div className="flex items-center gap-3">
                                <span className={`px-3 py-2 rounded-full ${getStatusColor(invoice.status)}`}>
                                    {getStatusText(invoice.status)}
                                </span>
                                <div>
                                    <span className="text-sm text-gray-600">
                                        {invoice.status === "SUBMITTED" && "En attente de votre paiement"}
                                        {invoice.status === "PAID" && "Payée le " + formatDate(invoice.paymentDate)}
                                        {invoice.status === "REJECTED" && "Facture rejetée"}
                                        {invoice.status === "REFUNDED" && "Remboursée"}
                                        {invoice.status === "OVERDUE" && "Échue, veuillez payer rapidement"}
                                    </span>
                                    {invoice.status === "SUBMITTED" && invoice.dueDate && (
                                        <div className="text-sm text-gray-500 mt-1">
                                            À payer avant le {formatDate(invoice.dueDate)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="font-bold text-lg mb-3">Détails de paiement</h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                {invoice.status === "PAID" ? (
                                    <>
                                        <p><strong>Mode de paiement:</strong> {invoice.paymentMethod || "Non spécifié"}</p>
                                        {invoice.transactionId && (
                                            <p><strong>Référence:</strong> {invoice.transactionId}</p>
                                        )}
                                        {invoice.paymentDate && (
                                            <p><strong>Date de paiement:</strong> {formatDateTime(invoice.paymentDate)}</p>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <p className="font-medium mb-2">Pour procéder au paiement:</p>
                                        <ol className="list-decimal pl-5 space-y-1 text-sm">
                                            <li>Cliquez sur "Payer maintenant" ci-dessus</li>
                                            <li>Choisissez votre méthode de paiement</li>
                                            <li>Confirmez la transaction</li>
                                            <li>Votre facture sera marquée comme payée</li>
                                        </ol>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer - Admin company info from credentials */}
                <div className="mt-10 pt-6 border-t text-center text-gray-600 text-sm">
                    <p className="font-medium">{credentials?.companyName || "Administration"}</p>

                    {credentials?.address && (
                        <p>Adresse: {credentials.address}</p>
                    )}

                    {credentials?.taxId && (
                        <p className="mt-2">TVA intracommunautaire: {credentials.taxId}</p>
                    )}

                    {credentials?.phoneNumber && (
                        <p>Contact: {credentials.phoneNumber}</p>
                    )}
                </div>

                {/* Notes section */}
                {invoice.notes && (
                    <div className="mt-6 pt-6 border-t">
                        <h3 className="font-bold text-lg mb-3">Notes</h3>
                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                            <p className="text-gray-700">{invoice.notes}</p>
                        </div>
                    </div>
                )}

                {/* Company help section */}
                <div className="mt-8 pt-6 border-t">
                    <h3 className="font-bold text-lg mb-3">Besoin d'aide ?</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <h4 className="font-medium text-blue-700 mb-2">
                                <i className="fas fa-question-circle mr-2"></i>
                                Questions sur la facture
                            </h4>
                            <p className="text-sm text-blue-600">
                                Contactez {credentials?.companyName || "l'administration"} pour toute question concernant cette facture.
                            </p>
                            {credentials?.phoneNumber && (
                                <p className="text-sm text-blue-600 mt-1">
                                    Téléphone: {credentials.phoneNumber}
                                </p>
                            )}
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                            <h4 className="font-medium text-green-700 mb-2">
                                <i className="fas fa-credit-card mr-2"></i>
                                Problème de paiement
                            </h4>
                            <p className="text-sm text-green-600">
                                En cas de problème avec le paiement, vérifiez votre méthode de paiement.
                            </p>
                            <p className="text-sm text-green-600 mt-1">
                                Vous pouvez également contacter le support technique.
                            </p>
                        </div>
                    </div>
                </div>
            </TaleDashbaordMainCard>
        </div>
    );
};

export default InvoiceDetails;
